import * as React from "react"
import { Grid, TextField, Typography, MenuItem, FormControlLabel, Switch, Accordion, AccordionSummary, AccordionDetails } from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"

import { scheduleFormData } from "./@signals"
import OwnSitesAutocomplete from "../Batches/CreateBatchDrawer/@components/OwnSitesAutocomplete"


const patchConfig = patch => {
    scheduleFormData.value = {
        ...scheduleFormData.value,
        config: { ...scheduleFormData.value.config, ...patch }
    }
}

/**
 * Execution options a recurrent batch needs and the schedule form never had: how far
 * back a product may have been seen, and whether the AI second pass runs on finish.
 * Anything left empty falls back to the house defaults the scheduler applies at run
 * time, so an existing schedule does not have to be rewritten to get them.
 */
export default function ScheduleExecutionFields({ disabled }) {
    const config = scheduleFormData.value.config
    const asp = config.autoSecondPass || {}
    const isWar2War = scheduleFormData.value.batch_type === 'war2war'
    const isRaw2Raw = scheduleFormData.value.batch_type === 'raw2raw'

    return (
        <Grid item xs={12}>
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">Execution options</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                                Leave empty to use the house defaults applied at execution time
                                (last seen within 90 days, AI second pass via Batch API at the
                                thresholds calibrated for this batch type, with the house 5%
                                control sample).
                            </Typography>
                        </Grid>

                        {isWar2War && (
                            <Grid item xs={12}>
                                {/* The only place a war2war schedule's own sites can be set.
                                    Bound to the SCHEDULE's config, not to the create-batch
                                    form: read from there they were unsettable, leaked from a
                                    half-open Create Batch drawer, and were wiped on every
                                    edit — and an empty list makes ia-normalizer fan the batch
                                    out over every site the customer has a catalog on. */}
                                <OwnSitesAutocomplete
                                    value={config.ownSites || []}
                                    onChange={ownSites => patchConfig({ ownSites })}
                                    disabled={disabled}
                                />
                            </Grid>
                        )}

                        {/* The novelties scope. Shown for both directions that anchor on a
                            "first seen in the last N days" window: war2war on competitor
                            listings, raw2raw on the customer's own products. Empty on
                            raw2raw means the WHOLE own catalogue — which is why the first
                            retail schedules anchored 67.341 Primor products a week instead
                            of the 1.198 that were actually new. */}
                        {(isWar2War || isRaw2Raw) && (
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Only products first seen in"
                                    helperText={isRaw2Raw
                                        ? 'Own products added in this window'
                                        : 'Competitor listings first seen in this window'}
                                    value={config.firstDateDays ?? ''}
                                    onChange={e => patchConfig({
                                        firstDateDays: e.target.value === '' ? null : parseInt(e.target.value, 10)
                                    })}
                                    disabled={disabled}
                                >
                                    <MenuItem value="">
                                        {isRaw2Raw ? 'No limit (whole catalogue)' : 'Default (30 days)'}
                                    </MenuItem>
                                    <MenuItem value={7}>7 days</MenuItem>
                                    <MenuItem value={14}>14 days</MenuItem>
                                    <MenuItem value={30}>30 days</MenuItem>
                                    <MenuItem value={90}>90 days</MenuItem>
                                </TextField>
                            </Grid>
                        )}

                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label="Exclude products not seen in"
                                value={config.maxDaysSinceSeen ?? ''}
                                onChange={e => patchConfig({
                                    maxDaysSinceSeen: e.target.value === '' ? null : parseInt(e.target.value, 10)
                                })}
                                disabled={disabled}
                            >
                                <MenuItem value="">Default (90 days)</MenuItem>
                                <MenuItem value={10}>10 days</MenuItem>
                                <MenuItem value={30}>30 days</MenuItem>
                                <MenuItem value={60}>60 days</MenuItem>
                                <MenuItem value={90}>90 days</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={!!asp.enabled}
                                        onChange={e => patchConfig({
                                            autoSecondPass: { ...asp, enabled: e.target.checked }
                                        })}
                                        disabled={disabled}
                                    />
                                }
                                label="Override the AI second pass settings"
                            />
                        </Grid>

                        {asp.enabled && (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="QA control sample (%)"
                                        helperText="Share of auto decisions left for human QA"
                                        value={asp.samplePct ?? 0}
                                        onChange={e => patchConfig({
                                            autoSecondPass: { ...asp, samplePct: e.target.value }
                                        })}
                                        disabled={disabled}
                                        inputProps={{ min: 0, max: 100, step: 1 }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={!!asp.speedMode}
                                                onChange={e => patchConfig({
                                                    autoSecondPass: { ...asp, speedMode: e.target.checked }
                                                })}
                                                disabled={disabled}
                                            />
                                        }
                                        label="Speed mode (immediate, ~2x the cost)"
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </Grid>
    )
}
